import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return null;
  const camelCaseObj = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
    camelCaseObj[camelKey] = obj?.[key];
  }
  return camelCaseObj;
};

// Helper function to convert array of objects from snake_case to camelCase
const toCamelCaseArray = (arr) => {
  if (!arr) return [];
  return arr?.map(obj => toCamelCase(obj));
};

export const adminService = {
  /**
   * Check if current user is an admin
   * @returns {Promise<boolean>}
   */
  async isAdmin() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('role')
        ?.eq('id', user?.id)
        ?.single();

      if (error) throw error;
      return data?.role === 'admin';
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  },

  /**
   * Get all users (admin only)
   * @returns {Promise<Array>}
   */
  async getAllUsers() {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return toCamelCaseArray(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  /**
   * Get user statistics
   * @returns {Promise<Object>}
   */
  async getUserStats() {
    try {
      const { data: allUsers, error: allError } = await supabase
        ?.from('user_profiles')
        ?.select('role, created_at, last_login');

      if (allError) throw allError;

      const totalUsers = allUsers?.length || 0;
      const adminCount = allUsers?.filter(u => u?.role === 'admin')?.length || 0;
      const regularCount = allUsers?.filter(u => u?.role === 'user')?.length || 0;

      // Calculate users active in last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo?.setDate(sevenDaysAgo?.getDate() - 7);
      const activeUsers = allUsers?.filter(u => {
        if (!u?.last_login) return false;
        return new Date(u?.last_login) >= sevenDaysAgo;
      })?.length || 0;

      return {
        totalUsers,
        adminCount,
        regularCount,
        activeUsers
      };
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }
  },

  /**
   * Create new admin user
   * @param {Object} userData - { email, password, fullName }
   * @returns {Promise<Object>}
   */
  async createAdminUser(userData) {
    try {
      const { email, password, fullName } = userData;

      // Create user with admin role in metadata
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: 'admin', // Trigger will extract this
            avatar_url: ''
          }
        }
      });

      if (error) throw error;
      return toCamelCase(data?.user);
    } catch (error) {
      console.error('Error creating admin user:', error);
      throw error;
    }
  },

  /**
   * Update user role (admin only)
   * @param {string} userId - UUID of user
   * @param {string} newRole - 'user' or 'admin'
   * @returns {Promise<Object>}
   */
  async updateUserRole(userId, newRole) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({ role: newRole })
        ?.eq('id', userId)
        ?.select()
        ?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  },

  /**
   * Get user by ID
   * @param {string} userId - UUID of user
   * @returns {Promise<Object>}
   */
  async getUserById(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', userId)
        ?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  },

  /**
   * Admin impersonation - login as another user
   * @param {string} userId - UUID of user to impersonate
   * @returns {Promise<Object>}
   */
  async impersonateUser(userId) {
    try {
      // First, verify the current user is an admin
      const isAdmin = await this.isAdmin();
      if (!isAdmin) {
        throw new Error('Unauthorized: Only admins can impersonate users');
      }

      // Get the target user's email
      const targetUser = await this.getUserById(userId);
      if (!targetUser) {
        throw new Error('User not found');
      }

      if (targetUser?.role === 'admin') {
        throw new Error('Cannot impersonate admin users');
      }

      // Store admin session info before impersonation
      const { data: { user: currentAdmin } } = await supabase?.auth?.getUser();
      
      // Store impersonation data in localStorage for tracking
      const impersonationData = {
        adminId: currentAdmin?.id,
        adminEmail: currentAdmin?.email,
        impersonatedUserId: userId,
        impersonatedUserEmail: targetUser?.email,
        startTime: new Date()?.toISOString()
      };
      
      localStorage.setItem('admin_impersonation', JSON.stringify(impersonationData));

      // Trigger a custom event to notify AuthContext
      window.dispatchEvent(new Event('storage'));
      
      // Return success
      return {
        success: true,
        message: 'Impersonation session started',
        impersonatedUser: targetUser
      };
    } catch (error) {
      console.error('Error impersonating user:', error);
      throw error;
    }
  },

  /**
   * End admin impersonation session
   * @returns {Promise<Object>}
   */
  async endImpersonation() {
    try {
      const impersonationData = localStorage.getItem('admin_impersonation');
      if (!impersonationData) {
        throw new Error('No active impersonation session');
      }

      const { adminEmail } = JSON.parse(impersonationData);
      
      // Clear impersonation data
      localStorage.removeItem('admin_impersonation');

      // Trigger storage event to update AuthContext
      window.dispatchEvent(new Event('storage'));

      return {
        success: true,
        message: 'Impersonation session ended',
        adminEmail
      };
    } catch (error) {
      console.error('Error ending impersonation:', error);
      throw error;
    }
  },

  /**
   * Check if current session is an impersonation
   * @returns {Object|null}
   */
  getImpersonationContext() {
    try {
      const impersonationData = localStorage.getItem('admin_impersonation');
      if (!impersonationData) return null;
      
      return JSON.parse(impersonationData);
    } catch (error) {
      console.error('Error getting impersonation context:', error);
      return null;
    }
  },

  /**
   * Get 7-day user activity data for chart
   * @returns {Promise<Array>}
   */
  async getSevenDayActivity() {
    try {
      const { data: allUsers, error } = await supabase
        ?.from('user_profiles')
        ?.select('created_at, last_login');

      if (error) throw error;

      // Generate last 7 days
      const days = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date?.setDate(date?.getDate() - i);
        date?.setHours(0, 0, 0, 0);
        
        const nextDate = new Date(date);
        nextDate?.setDate(nextDate?.getDate() + 1);
        
        // Count new users created on this day
        const newUsers = allUsers?.filter(u => {
          const createdDate = new Date(u?.created_at);
          return createdDate >= date && createdDate < nextDate;
        })?.length || 0;

        // Count users who logged in on this day
        const logins = allUsers?.filter(u => {
          if (!u?.last_login) return false;
          const loginDate = new Date(u?.last_login);
          return loginDate >= date && loginDate < nextDate;
        })?.length || 0;

        days?.push({
          date: date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          newUsers,
          logins
        });
      }

      return days;
    } catch (error) {
      console.error('Error fetching 7-day activity:', error);
      throw error;
    }
  },

  /**
   * Get admin users only
   * @returns {Promise<Array>}
   */
  async getAdminUsers() {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('role', 'admin')
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return toCamelCaseArray(data);
    } catch (error) {
      console.error('Error fetching admin users:', error);
      throw error;
    }
  },

  /**
   * Get regular users only (non-admins)
   * @returns {Promise<Array>}
   */
  async getRegularUsers() {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('role', 'user')
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return toCamelCaseArray(data);
    } catch (error) {
      console.error('Error fetching regular users:', error);
      throw error;
    }
  },

  /**
   * Delete user and all their data (admin only)
   * @param {string} userId - UUID of user to delete
   * @returns {Promise<Object>} - Deletion summary
   */
  async deleteUser(userId) {
    try {
      // Call the database function for cascading delete
      const { data, error } = await supabase
        ?.rpc('admin_delete_user_and_data', {
          target_user_id: userId
        });

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }
};